import React, { useState } from "react"

import { Loader2 } from "lucide-react"
import { useForm } from "react-hook-form"
import { toast } from "sonner"

import Icon from "@/components/Icon"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useAuth } from "@/contexts/AuthContext"
import { useCreateDiaryEntry } from "@/hooks/useCreateDiaryEntry"
import { useFetchDiaryEntries } from "@/hooks/useFetchDiaryEntries"
import { useFetchUniform } from "@/hooks/useFetchUniform"
import { useIssueUniform } from "@/hooks/useIssueUniform"
import { Person } from "@/types/person"

interface UniformTabProps {
  person: Person
}

interface IssueItemForm {
  itemType: string
  size: string
  dateGiven: string
  quantity: number
}

// Airtable record IDs for uniform items
const UNIFORM_ITEM_IDS = {
  smallTShirt: "reckx2crNZFfpKhBm", // Small T-shirt
  mediumTShirt: "recEwThErbMv1evLU", // Medium T-shirt
  largeTShirt: "recCMUz6oQYS6EoU1", // Large T-shirt
  extraLargeTShirt: "rece7hOQm94Z0We16", // Extra Large T-shirt
  cap: "reco49UU2YoDLkqR2", // Cap
  smallJacket: "rec3nwHX7Qs9a99EF", // Small Jacket
  mediumJacket: "recsbKeBAYGVblulv", // Medium Jacket
  largeJacket: "rec0doles6QiAMlQm", // Large Jacket
  extraLargeJacket: "recYjqRnjDau0FO6d", // Extra Large Jacket
  chargers: "rec1bvm2wFnf9Wxr4", // Chargers
  phoneHolders: "recMMPodzvtLXqGRr", // Phone Holders
}

const uniformItems = [
  {
    key: "smallTShirt",
    label: "Small T-shirt",
    id: UNIFORM_ITEM_IDS.smallTShirt,
  },
  {
    key: "mediumTShirt",
    label: "Medium T-shirt",
    id: UNIFORM_ITEM_IDS.mediumTShirt,
  },
  {
    key: "largeTShirt",
    label: "Large T-shirt",
    id: UNIFORM_ITEM_IDS.largeTShirt,
  },
  {
    key: "extraLargeTShirt",
    label: "Extra Large T-shirt",
    id: UNIFORM_ITEM_IDS.extraLargeTShirt,
  },
  {
    key: "smallJacket",
    label: "Small Jacket",
    id: UNIFORM_ITEM_IDS.smallJacket,
  },
  {
    key: "mediumJacket",
    label: "Medium Jacket",
    id: UNIFORM_ITEM_IDS.mediumJacket,
  },
  {
    key: "largeJacket",
    label: "Large Jacket",
    id: UNIFORM_ITEM_IDS.largeJacket,
  },
  {
    key: "extraLargeJacket",
    label: "Extra Large Jacket",
    id: UNIFORM_ITEM_IDS.extraLargeJacket,
  },

  { key: "cap", label: "Cap", id: UNIFORM_ITEM_IDS.cap },
  { key: "chargers", label: "Chargers", id: UNIFORM_ITEM_IDS.chargers },
  {
    key: "phoneHolders",
    label: "Phone Holders",
    id: UNIFORM_ITEM_IDS.phoneHolders,
  },
]

const diaryBackedItems = [
  { key: "hiVis", label: "Hi Vis", subTag: "Uniform - Hi Vis" },
  { key: "safetyBoots", label: "Safety Boots", subTag: "Uniform - Safety Boots" },
]

const UniformTab: React.FC<UniformTabProps> = ({ person }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [expanded, setExpanded] = useState<{ [key: string]: boolean }>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { user, claims } = useAuth()

  const teamPermissions = claims.team_permissions || []
  const canEditUniform = teamPermissions.includes("update.tab.uniform")

  const { data: uniformData, isLoading } = useFetchUniform({
    driverId: person.person_id,
  })

  const { issueUniform } = useIssueUniform()

  const createDiaryEntry = useCreateDiaryEntry()
  const { data: diaryData } = useFetchDiaryEntries({
    personId: person.id,
    engagementTag: "Uniform",
  })

  const form = useForm<IssueItemForm>({
    defaultValues: {
      itemType: "",
      size: "",
      quantity: 1,
    },
  })

  const onSubmit = async (data: IssueItemForm) => {
    try {
      setIsSubmitting(true)

      const diaryItem = diaryBackedItems.find((item) => item.key === data.itemType)

      if (diaryItem) {
        const givenBy = user?.full_name || user?.email || "Unknown"
        const notes = `${diaryItem.label} issued (x${data.quantity}) by ${givenBy}`

        await createDiaryEntry.mutateAsync({
          personId: person.id,
          notes,
          driverOpsTag: "Uniform",
          driverOpsSubTag: [diaryItem.subTag],
          callDateTime: new Date().toISOString(),
        })

        toast.success("Success", {
          description: `${diaryItem.label} has been issued to ${person.first_name} ${person.surname}`,
        })
      } else {
        const uniformItemName =
          data.itemType === "tShirt" || data.itemType === "jacket"
            ? `${data.size} ${data.itemType === "tShirt" ? "T-shirt" : "Jacket"}`
            : data.itemType

        const selectedUniformItem = uniformItems.find((item) => item.key === data.itemType)
        if (!selectedUniformItem?.id) {
          throw new Error("Invalid uniform item selected")
        }

        await issueUniform({
          driverId: person.person_id,
          uniformItem: selectedUniformItem.id,
          quantity: data.quantity,
          givenBy: user?.full_name || user?.email || "Unknown",
        })

        toast.success("Success", {
          description: `${uniformItemName} has been issued to ${person.first_name} ${person.surname}`,
        })
      }

      setIsDialogOpen(false)
      form.reset()
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : "Unknown error"
      console.error("Error issuing uniform item:", err)
      toast.error("Error", {
        description: errorMessage,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">
          Uniform & Equipment
          {isLoading && (
            <Loader2 className="ml-2 h-4 w-4 inline animate-spin text-palette-jungleGreen500" />
          )}
        </h3>
        {canEditUniform && (
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm">
                <Icon name="plus" className="h-4 w-4 mr-2" />
                Issue Item
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Issue Uniform Item</DialogTitle>
              </DialogHeader>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="itemType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Item Type</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select item type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {uniformItems.map((item) => (
                              <SelectItem key={item.key} value={item.key}>
                                {item.label}
                              </SelectItem>
                            ))}
                            {diaryBackedItems.map((item) => (
                              <SelectItem key={item.key} value={item.key}>
                                {item.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="quantity"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Quantity</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min="1"
                            step="1"
                            {...field}
                            onChange={(e) => field.onChange(parseInt(e.target.value) || 1)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="flex justify-end space-x-2">
                    <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={isSubmitting}>
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Issuing...
                        </>
                      ) : (
                        <>
                          <Icon name="check" className="mr-2 h-4 w-4" />
                          Issue Item
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </Form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Uniform Issued</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <Loader2 className="h-8 w-8 animate-spin text-palette-jungleGreen400" />
              <p className="text-sm text-palette-jungleGreen500">Loading uniform data...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {uniformItems.map((item) => {
                const issues = (uniformData?.data || []).filter(
                  (issue) => issue.uniformItem === item.id,
                )
                const latest = issues[issues.length - 1]
                return (
                  <div
                    key={item.key}
                    className="bg-white rounded-lg shadow p-4 flex flex-col gap-2 border relative"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="font-semibold text-palette-jungleGreen800">{item.label}</div>
                      <Badge
                        className={
                          latest
                            ? "bg-green-100 text-green-800 hover:bg-green-100"
                            : "bg-red-100 text-red-800 hover:bg-red-100"
                        }
                      >
                        {latest ? "Yes" : "No"}
                      </Badge>
                    </div>
                    {latest ? (
                      <>
                        <div className="text-sm text-palette-jungleGreen600">
                          Issued By:{" "}
                          <span className="text-palette-jungleGreen900">{latest.givenBy}</span>
                        </div>
                        <div className="text-sm text-palette-jungleGreen600">
                          Issue Date:{" "}
                          <span className="text-palette-jungleGreen900">
                            {latest.dateGiven ? new Date(latest.dateGiven).toLocaleString() : "N/A"}
                          </span>
                        </div>
                      </>
                    ) : (
                      <div className="text-sm text-palette-jungleGreen600">Not issued</div>
                    )}
                    <button
                      className="mt-2 text-xs text-blue-600 flex items-center gap-1 hover:underline"
                      onClick={() => setExpanded((e) => ({ ...e, [item.key]: !e[item.key] }))}
                      type="button"
                    >
                      {expanded[item.key] ? (
                        <Icon name="caretUp" className="w-4 h-4" />
                      ) : (
                        <Icon name="caretDown" className="w-4 h-4" />
                      )}
                      {expanded[item.key] ? "Hide History" : "Show History"}
                    </button>
                    {expanded[item.key] && (
                      <div className="mt-2 border-t pt-2">
                        <div className="text-xs font-semibold text-palette-jungleGreen700 mb-1">
                          Issue History:
                        </div>
                        {issues.length === 0 ? (
                          <div className="text-xs text-palette-jungleGreen500 italic">
                            No issue history recorded.
                          </div>
                        ) : (
                          <ul className="text-xs text-palette-jungleGreen700 space-y-1">
                            {issues
                              .slice(0, -1)
                              .reverse()
                              .map((issue, idx) => (
                                <li key={idx}>
                                  <div>
                                    Date:{" "}
                                    <span className="font-mono">
                                      {issue.dateGiven
                                        ? new Date(issue.dateGiven).toLocaleString()
                                        : "N/A"}
                                    </span>
                                  </div>
                                  <div>
                                    By: <span className="font-mono break-all">{issue.givenBy}</span>
                                  </div>
                                </li>
                              ))}
                            {issues.length === 1 && (
                              <li className="flex gap-4 text-palette-jungleGreen500 italic">
                                Only one issue recorded.
                              </li>
                            )}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
              {diaryBackedItems.map((item) => {
                const entries = (diaryData?.data || []).filter((entry) =>
                  entry.driverOpsSubTag?.includes(item.subTag),
                )
                const latest = entries[0]
                return (
                  <div
                    key={item.key}
                    className="bg-white rounded-lg shadow p-4 flex flex-col gap-2 border relative"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="font-semibold text-palette-jungleGreen800">{item.label}</div>
                      <Badge
                        className={
                          latest
                            ? "bg-green-100 text-green-800 hover:bg-green-100"
                            : "bg-red-100 text-red-800 hover:bg-red-100"
                        }
                      >
                        {latest ? "Yes" : "No"}
                      </Badge>
                    </div>
                    {latest ? (
                      <>
                        <div className="text-sm text-palette-jungleGreen600">
                          Issued By:{" "}
                          <span className="text-palette-jungleGreen900">
                            {latest.authorEmail || latest.author}
                          </span>
                        </div>
                        <div className="text-sm text-palette-jungleGreen600">
                          Issue Date:{" "}
                          <span className="text-palette-jungleGreen900">
                            {latest.callDateTime
                              ? new Date(latest.callDateTime).toLocaleString()
                              : "N/A"}
                          </span>
                        </div>
                      </>
                    ) : (
                      <div className="text-sm text-palette-jungleGreen600">Not issued</div>
                    )}
                    <button
                      className="mt-2 text-xs text-blue-600 flex items-center gap-1 hover:underline"
                      onClick={() => setExpanded((e) => ({ ...e, [item.key]: !e[item.key] }))}
                      type="button"
                    >
                      {expanded[item.key] ? (
                        <Icon name="caretUp" className="w-4 h-4" />
                      ) : (
                        <Icon name="caretDown" className="w-4 h-4" />
                      )}
                      {expanded[item.key] ? "Hide History" : "Show History"}
                    </button>
                    {expanded[item.key] && (
                      <div className="mt-2 border-t pt-2">
                        <div className="text-xs font-semibold text-palette-jungleGreen700 mb-1">
                          Issue History:
                        </div>
                        {entries.length === 0 ? (
                          <div className="text-xs text-palette-jungleGreen500 italic">
                            No issue history recorded.
                          </div>
                        ) : (
                          <ul className="text-xs text-palette-jungleGreen700 space-y-1">
                            {entries.map((entry, idx) => (
                              <li key={idx}>
                                <div>
                                  Date:{" "}
                                  <span className="font-mono">
                                    {entry.callDateTime
                                      ? new Date(entry.callDateTime).toLocaleString()
                                      : "N/A"}
                                  </span>
                                </div>
                                <div>
                                  By:{" "}
                                  <span className="font-mono break-all">
                                    {entry.authorEmail || entry.author}
                                  </span>
                                </div>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default UniformTab
